import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, tap, throwError } from 'rxjs';

import {
  AuthSessionResponse,
  AuthTokens,
  AuthenticatedUser,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  GoogleLoginRequest,
  GoogleRegisterRequest,
  LoginRequest,
  LoginResponse,
  MagicLoginRequest,
  MagicLoginRequestResponse,
  MagicLoginVerifyRequest,
  RegisterRequest,
  RegisterResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  UserRole
} from '../models';

interface PersistedAuthState {
  user: AuthenticatedUser | null;
  tokens: AuthTokens | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly storageKey = 'gasolinaRadar:auth';
  private isLoggingOut = false;
  private inFlightRefresh: Observable<AuthTokens> | null = null;

  private readonly userState: WritableSignal<AuthenticatedUser | null> = signal(null);
  private readonly tokensState: WritableSignal<AuthTokens | null> = signal(null);

  readonly user: Signal<AuthenticatedUser | null> = this.userState.asReadonly();
  readonly accessToken = computed(() => this.tokensState()?.accessToken ?? null);
  readonly refreshToken = computed(() => this.tokensState()?.refreshToken ?? null);
  readonly isAuthenticated = computed(() => Boolean(this.tokensState()?.accessToken && this.userState()));

  constructor() {
    this.restoreFromStorage();
  }

  register(payload: RegisterRequest): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>('auth/register', payload).pipe(
      catchError((error) => this.handleError('No se pudo completar el registro.', error))
    );
  }

  login(payload: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>('auth/login', payload).pipe(
      tap((response) => {
        if (this.isSessionResponse(response)) {
          this.persistAuthState(response);
        }
      }),
      catchError((error) => this.handleError('No se pudo iniciar sesión.', error))
    );
  }

  loginWithGoogle(payload: GoogleLoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>('auth/login/google', payload).pipe(
      tap((response) => {
        if (this.isSessionResponse(response)) {
          this.persistAuthState(response);
        }
      }),
      catchError((error) => this.handleError('No se pudo iniciar sesión con Google.', error))
    );
  }

  forgotPassword(payload: ForgotPasswordRequest): Observable<ForgotPasswordResponse> {
    return this.http
      .post<ForgotPasswordResponse>('auth/forgot-password', payload)
      .pipe(catchError((error) => this.handleError('No se pudo solicitar la recuperación.', error)));
  }

  resetPassword(payload: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    return this.http
      .post<ResetPasswordResponse>('auth/reset-password', payload)
      .pipe(catchError((error) => this.handleError('No se pudo restablecer la contraseña.', error)));
  }

  requestMagicLogin(payload: MagicLoginRequest): Observable<MagicLoginRequestResponse> {
    return this.http
      .post<MagicLoginRequestResponse>('auth/login/magic/request', payload)
      .pipe(catchError((error) => this.handleError('No se pudo solicitar el acceso mágico.', error)));
  }

  verifyMagicLogin(payload: MagicLoginVerifyRequest): Observable<AuthSessionResponse> {
    return this.http.post<AuthSessionResponse>('auth/login/magic/verify', payload).pipe(
      tap((response) => this.persistAuthState(response)),
      catchError((error) => this.handleError('No se pudo validar el acceso mágico.', error))
    );
  }

  registerWithGoogle(payload: GoogleRegisterRequest): Observable<AuthSessionResponse> {
    return this.http.post<AuthSessionResponse>('auth/register/google', payload).pipe(
      tap((response) => this.persistAuthState(response)),
      catchError((error) => this.handleError('No se pudo completar el registro con Google.', error))
    );
  }

  changePassword(payload: ChangePasswordRequest): Observable<ResetPasswordResponse> {
    return this.http
      .post<ResetPasswordResponse>('auth/change-password', payload)
      .pipe(catchError((error) => this.handleError('No se pudo cambiar la contraseña.', error)));
  }

  refresh(): Observable<AuthTokens> {
    const token = this.refreshToken();

    if (!token) {
      return throwError(() => new Error('Refresh token no disponible'));
    }

    if (this.inFlightRefresh) {
      return this.inFlightRefresh;
    }

    this.inFlightRefresh = this.http
      .post<AuthSessionResponse>('auth/refresh', { refreshToken: token })
      .pipe(
        tap((response) => this.persistAuthState(response)),
        map(({ accessToken, refreshToken }) => ({ accessToken, refreshToken })),
        catchError((error) => this.handleError('No se pudo renovar la sesión.', error)),
        finalize(() => {
          this.inFlightRefresh = null;
        })
      );

    return this.inFlightRefresh;
  }

  logout(redirectToStations = true): void {
    if (this.isLoggingOut) {
      return;
    }

    const refreshToken = this.refreshToken();

    if (!refreshToken) {
      this.clearAuthState();
      if (redirectToStations) {
        void this.router.navigate(['/stations']);
      }
      return;
    }

    this.isLoggingOut = true;

    this.http
      .post<void>('auth/logout', { refreshToken })
      .pipe(
        catchError((error) => {
          console.error('No se pudo cerrar sesión en el servidor.', error);
          return of(void 0);
        }),
        finalize(() => {
          this.clearAuthState();
          this.isLoggingOut = false;
          if (redirectToStations) {
            void this.router.navigate(['/stations']);
          }
        })
      )
      .subscribe();
  }

  updateUser(user: AuthenticatedUser): void {
    this.userState.set(user);

    const tokens = this.tokensState();

    if (tokens?.accessToken && tokens?.refreshToken) {
      const payload: PersistedAuthState = { user, tokens };
      localStorage.setItem(this.storageKey, JSON.stringify(payload));
    }
  }

  hasRole(roles: UserRole[]): boolean {
    const currentRole = this.userState()?.role;
    return currentRole ? roles.includes(currentRole) : false;
  }

  private persistAuthState(response: AuthSessionResponse): void {
    const { user, accessToken, refreshToken } = response;

    if (!accessToken || !refreshToken) {
      return;
    }

    const tokens: AuthTokens = { accessToken, refreshToken };

    this.userState.set(user);
    this.tokensState.set(tokens);

    const payload: PersistedAuthState = { user, tokens };
    localStorage.setItem(this.storageKey, JSON.stringify(payload));
  }

  private clearAuthState(): void {
    this.userState.set(null);
    this.tokensState.set(null);
    localStorage.removeItem(this.storageKey);
  }

  private restoreFromStorage(): void {
    const raw = localStorage.getItem(this.storageKey);

    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw) as PersistedAuthState;

      if (parsed?.user && parsed?.tokens?.accessToken && parsed?.tokens?.refreshToken) {
        this.userState.set(parsed.user);
        this.tokensState.set(parsed.tokens);
      }
    } catch (error) {
      console.warn('No se pudo restaurar el estado de autenticación', error);
      localStorage.removeItem(this.storageKey);
    }
  }

  private handleError(message: string, error: unknown) {
    console.error(message, error);
    return throwError(() => (error instanceof HttpErrorResponse ? error : new Error(message)));
  }

  private isSessionResponse(response: LoginResponse): response is AuthSessionResponse {
    return Boolean((response as AuthTokens | undefined)?.accessToken && (response as AuthTokens)?.refreshToken);
  }
}
